// User-facing copy for the admin pricing area, kept local to the feature rather
// than in the shared strings.constant so the admin surface stays self-contained.
// No em dash, en dash, or "--", same rule the dash check enforces app-wide.

export const ADMIN_PATH = '/admin';

export const ADMIN_AREA_TITLE = 'Administration';
export const ADMIN_NAV_LABEL = 'Admin';
export const ADMIN_NO_ACCESS = 'You do not have access to any admin tools.';

export const SECTION_PLATFORM_DEFAULTS = 'Platform defaults';
export const SECTION_OWNER_RATE_CARD = 'Owner rate cards';
export const SECTION_VIDEO_OVERRIDE = 'Single video';

// The rate grid
export const COL_USAGE = 'Usage';
export const COL_TERRITORY = 'Territory';
export const COL_PERIOD = 'Period';
export const COL_RATE = 'Rate, USD per second';
export const COL_SOURCE = 'Source';
export const SOURCE_SET_HERE = 'Set here';
export const SOURCE_DEFAULT = 'Codeword default';
export const SOURCE_OWNER = 'Owner rate card';
export const INHERIT_HINT = (value: number, source: string): string => `Inherits ${value} from the ${source}`;

// The served pricing_source, as a label. The level a rate actually resolved
// from is the server's answer, not something a screen should assume: an asset
// falls through to its owner's card OR, where the owner also inherits, to the
// Codeword default, and only pricing_source distinguishes those.
export const sourceLabel = (source: string): string =>
  source === 'custom_account_pricing' ? SOURCE_OWNER : SOURCE_DEFAULT;

// Validation
export const ERROR_REQUIRED = 'A rate is required here.';
export const ERROR_NEGATIVE = 'A rate cannot be negative.';
export const ERROR_NOT_A_NUMBER = 'Enter a number.';

// Save and load
export const SAVE = 'Save changes';
export const SAVING = 'Saving';
export const SAVED = 'Saved.';
export const SAVE_FAILED = 'Could not save. Please try again.';
export const LOADING = 'Loading';
export const LOAD_FAILED = 'Could not load pricing. Please try again.';

// Platform defaults
export const PLATFORM_DEFAULTS_HELP =
  'These rates apply to every owner who has not set their own. They are the bottom of the chain, so every one of the eight must be filled in.';

// Owner rate card
export const OWNER_PICKER_LABEL = 'Content owner';
export const ACCOUNT_MODE_LABEL = 'Account pricing';
export const ACCOUNT_MODE_INHERIT = 'Use Codeword suggested pricing';
export const ACCOUNT_MODE_CUSTOM = 'Set custom account pricing';
export const OWNER_RATE_CARD_HELP =
  'A blank rate inherits the Codeword default, shown in grey. A rate set here overrides it for this owner only.';
export const summarySetInheriting = (set: number, inheriting: number): string =>
  `${set} of 8 set by this owner, ${inheriting} inheriting the Codeword default.`;

// Single video
// "Video ID", with capitals (Joey, 2026-10-13). Until then these two read
// "Video id" and "a video id". Text only.
export const VIDEO_ID_LABEL = 'Video ID';
export const NO_VIDEO_LOADED = 'Enter a video ID and choose Load to see its rates.';
export const ASSET_MODE_LABEL = 'Video pricing';
export const ASSET_MODE_INHERIT = 'Inherit from the owner';
export const ASSET_MODE_CUSTOM = 'Set a custom price for this video';
export const ASSET_MODE_QUOTE = 'Priced on request';
export const ASSET_MODE_NOT_LICENSABLE = 'Not licensable';
export const VIDEO_RATE_CARD_HELP =
  'A blank rate inherits the owner rate card, or the Codeword default where the owner also inherits. The grey hint shows which.';
export const NO_PRICES_QUOTE = 'This video is priced on request. It has no self-service rates.';
export const NO_PRICES_NOT_LICENSABLE = 'This video is not available for licensing. It has no rates.';

// ---------------------------------------------------------------------------
// Support order lookup
// ---------------------------------------------------------------------------
export const SECTION_ORDER_LOOKUP = 'Find an order';
export const ORDER_LOOKUP_HELP =
  'Enter the email address the buyer contacted you from. Every order under that address is shown, whatever its status, with the confirmation number they can see on their screen.';
export const ORDER_LOOKUP_EMAIL_LABEL = 'Buyer email address';
export const ORDER_LOOKUP_SEARCH = 'Find orders';
export const ORDER_LOOKUP_SEARCHING = 'Searching';
export const ORDER_LOOKUP_NO_MATCH =
  'No account holds that address. Check the spelling, or ask the buyer which address they used.';
export const ORDER_LOOKUP_NO_ORDERS = 'This account has no orders.';
export const ORDER_LOOKUP_TWO_ACCOUNTS =
  'More than one account holds this address. Both are shown below.';
export const COL_CONFIRMATION = 'Confirmation';
export const COL_TITLE = 'Video';
export const COL_TIMECODES = 'Timecodes';
export const COL_LENGTH = 'Billed';
export const COL_LICENCE = 'Licence';
export const COL_PAID = 'Paid';
export const COL_WHEN = 'Purchased';
export const COL_STATUS = 'Status';
export const COL_CLIP = 'Clip';
export const COL_PAYMENT = 'Stripe payment';
export const CLIP_DELIVERED = 'Delivered';
export const CLIP_NOT_DELIVERED = 'Not delivered';
export const NO_PAYMENT_REFERENCE = 'No payment reference';
export const secondsLabel = (seconds: number | null): string =>
  seconds === null ? 'Not recorded' : `${seconds}s`;

// ---------------------------------------------------------------------------
// User roles
// ---------------------------------------------------------------------------
export const SECTION_USER_ROLES = 'User roles';
// Item 9 (28 Oct): one row per person, opened to see their details and give or change their role. DRAFT (Vanja).
export const USER_ROLES_HELP =
  'Open a person to see what they told us at sign-up and to give or change their role. A role is always given on a company.';
export const NO_ROLE = 'No role';
export const NO_TENANT = 'No tenant';
export const NEVER_SIGNED_IN = 'Never';
export const USER_DISABLED = 'Disabled';
export const LOAD_MORE = 'Load more';

// ---- The searchable list (28 Oct, PR (b) B5). DRAFT (Vanja) ----
export const USERS_SEARCH_LABEL = 'Search by email, name or company';
export const USERS_SEARCH_TOO_LONG = 'Use at most 100 characters.';
export const USERS_SEARCH_FILTERS_OFF = 'Other filters are not used while searching.';
export const QUICK_ALL = 'All';
export const QUICK_WAITING = 'Waiting for access';
export const QUICK_DISABLED = 'Disabled';
export const QUICK_FILTERS_LABEL = 'Show';
// A count the server could not read is a dash, never 0.
export const quickFilterLabel = (label: string, count: number | null): string =>
  `${label} (${count === null ? '\u2013' : count.toLocaleString('en-GB')})`;
export const FILTER_LABEL = 'Also filter by';
export const FILTER_NONE = 'Nothing else';
export const FILTER_ROLE = 'Role';
export const FILTER_COMPANY = 'Company';
export const FILTER_VERIFIED = 'Email';
export const FILTER_VERIFIED_YES = 'Confirmed';
export const FILTER_VERIFIED_NO = 'Not confirmed';
export const FILTER_COMPANY_KIND = 'Kind of company';
export const FILTER_CLEAR = 'Clear';
export const USERS_LOADING = 'Loading people...';
export const USERS_EMPTY = 'Nobody matches.';
export const USERS_EMPTY_PAGE_MORE = 'Nobody on this page matched. There may be more: load more to look further.';
export const USERS_LOAD_FAILED = "The list couldn't be loaded.";
export const USERS_TRY_AGAIN = 'Try again';
export const USERS_COUNTS_FAILED = "The numbers couldn't be loaded.";

// The closed row (Joey, 29 Oct: less jargon): the role, or this when there is none; and Disabled when disabled.
export const WAITING_FOR_ACCESS = 'Waiting for access';
export const STATUS_DISABLED = 'Disabled';

// The company, always with its kind (Joey, 29 Oct: names are unique per kind, so the kind shows beside every company
// name an admin sees or picks). A platform role is not limited to a company: "All companies" (Joey, 29 Oct). A company
// id that is not on the company list (`yh`) shows as the id, plainly.
export const NO_COMPANY = 'No company';
export const ALL_COMPANIES = 'All companies';
export const companyKindShort = (kind: 'buyer' | 'licensor'): string => (kind === 'buyer' ? 'Licensee' : 'Licensor');

// Dates: "28 Oct 2026"; the full date and time on hover.
export const DETAILS_SIGNED_UP = 'Signed up';
export const DETAILS_EMAIL = 'Email address';
export const DETAILS_EMAIL_CONFIRMED = 'confirmed';
export const DETAILS_EMAIL_NOT_CONFIRMED = 'not confirmed yet';
export const DATE_UNKNOWN = '\u2013';

// The list's index ("Build the user list", Platform Admin; B3).
export const INDEX_NOT_BUILT =
  'The user list has not been built with its current layout, so search and filters may miss people. Build it once; it takes a few minutes for every few thousand accounts.';
export const INDEX_STATE_UNKNOWN = "The state of the user list couldn't be read. Search and filters may miss people.";
export const INDEX_BUILD = 'Build the user list';
export const INDEX_BUILD_CONTINUE = 'Continue building';
export const indexBuildProgress = (accounts: number, phase: 'users' | 'orphans'): string =>
  phase === 'users'
    ? `Building: ${accounts.toLocaleString('en-GB')} accounts read so far.`
    : 'Building: removing rows of accounts that no longer exist.';
export const INDEX_BUILD_DONE = 'The user list is built.';
// The build ended while a person was open: the list is not reloaded under them (audit G-1, 29 Oct).
export const INDEX_BUILD_DONE_KEPT = 'The user list is built. The person you have open stays open until you show the new list.';
export const INDEX_BUILD_SHOW_NEW = 'Show the new list';
export const INDEX_BUILD_STOPPED = 'The build stopped. Nothing was lost: continue to pick up where it stopped.';

// ---- The opened row (item 9, the admin plan's B.3). DRAFT (Vanja) ----
export const EMAIL_CONFIRMED = 'Email confirmed';
export const EMAIL_NOT_CONFIRMED = 'Email not confirmed';
export const DETAILS_HEADING = 'Details';
export const DETAILS_GIVEN_NOTE = 'What they told us at sign-up';
export const DETAILS_NAME = 'Name';
export const DETAILS_COMPANY_TYPED = 'Company';
export const DETAILS_NONE_GIVEN = 'Nothing given';
export const DETAILS_LOADING = 'Loading details...';
export const DETAILS_LOAD_FAILED = "The details couldn't be loaded.";
export const DETAILS_TRY_AGAIN = 'Try again';
export const DETAILS_COMPANY_ON_ACCOUNT = 'Company on the account';
export const DETAILS_LAST_SIGNED_IN = 'Last signed in';

export const ACCESS_HEADING = 'Access';
export const ACCESS_ACTION_GRANT = 'Give a role';
export const ACCESS_ACTION_CHANGE = 'Change role';
export const ACCESS_PLATFORM_NOTE = 'This person holds a platform role. It cannot be changed here.';
export const ACCESS_NEEDS_CONFIRMED_EMAIL =
  'They must confirm their email address before a role can be given. They can send the email again from their profile page.';
export const ACCESS_ROLE_LABEL = 'Role';
export const ACCESS_COMPANY_LABEL = 'Company';
export const ACCESS_COMPANY_FIXED_NOTE = 'Change company comes later.';
export const accessTypedCompanyHint = (company: string): string => `They typed "${company}" at sign-up (a suggestion only).`;
export const ACCESS_CHOOSE_ROLE_FIRST = 'Choose a role first: it decides which companies can be chosen.';
export const ACCESS_APPLY = 'Save';
export const ACCESS_CANCEL = 'Cancel';
export const accessDone = (who: string, role: string, company: string): string =>
  `${who} now has the role ${role} on ${company}. They will need to sign in again.`;
export const ACCESS_FAILED = 'The change was not made.';
// A failure after which the change may have happened (a server error, a lost answer): never "not made".
export const ACCESS_RELOAD_HINT = 'If their access changed since this list loaded, reload the list to see it.';
export const ACCESS_MAYBE_DONE = 'The change may have been made. Reload the list to see their current role before trying again.';

// The confirmation before a first role (no typing: nothing is taken away).
export const GRANT_DIALOG_TITLE = 'Give this role?';
export const grantDialogBody = (who: string, role: string, company: string): string =>
  `${who} will have the role ${role} on ${company}. They will need to sign in again before it takes effect.`;
export const GRANT_DIALOG_CONFIRM = 'Give role';

// The confirmation before a role is REPLACED: the admin types the person's email (the admin plan's Q8).
export const REPLACE_DIALOG_TITLE = 'Replace this role?';
export const replaceDialogBody = (who: string, from: string, to: string, company: string): string =>
  `${who} has the role ${from}. It will be replaced by ${to} on ${company}, which takes away what ${from} allowed. They will need to sign in again.`;
export const REPLACE_DIALOG_CONFIRM = 'Replace role';
export const typeEmailLabel = (email: string): string => `Type ${email} to confirm`;
export const TYPE_EMAIL_MISMATCH = 'This does not match their email address.';

// The company picker.
export const COMPANY_SEARCH_LABEL = 'Search companies';
export const companyKindLabel = (kind: 'buyer' | 'licensor'): string => (kind === 'buyer' ? 'Licensee company' : 'Licensor company');
export const companyOption = (name: string, tenantId: string): string => `${name} (${tenantId})`;
export const COMPANY_NONE_FOUND = 'No company matches.';
export const COMPANY_LOADING = 'Loading companies...';
export const COMPANY_LOAD_FAILED = "The companies couldn't be loaded.";
export const COMPANY_TRY_AGAIN = 'Try again';
export const COMPANY_LOAD_MORE = 'More companies';
export const COMPANY_CHOSEN = 'Chosen';
export const COMPANY_ADD = 'Add a company';
export const COMPANY_ADD_NAME = 'Company name';
export const COMPANY_ADD_ID = 'Company id';
export const COMPANY_ADD_ID_HELP = 'Lower-case letters, digits and dashes. It cannot be changed later.';
export const COMPANY_ADD_ID_INVALID = 'Use 1 to 64 lower-case letters, digits and dashes, starting and ending with a letter or digit.';
export const COMPANY_ADD_NAME_INVALID = 'Enter a name of 1 to 120 characters.';
export const companyAddKindNote = (kind: 'buyer' | 'licensor'): string =>
  kind === 'buyer' ? 'It will be a licensee (buyer) company.' : 'It will be a licensor company.';
export const COMPANY_ADD_SAVE = 'Add company';
export const COMPANY_ADD_CANCEL = 'Cancel';
export const COMPANY_ADD_FAILED = 'The company was not added.';

// The confirmation dialog. It names the person, the current role and the new
// role in words, because the panel is a table of similar-looking rows and the
// realistic accident is acting on the row above or below the intended one.
// "Are you sure" repeats nothing the reader could check against.
export const ROLE_DIALOG_CANCEL = 'Cancel';

// Role keys are not shown to a person. These are the workbook's own names.
export const ROLE_LABELS: Record<string, string> = {
  'cw-platform-admin': 'Platform Admin',
  'cw-platform-ops': 'Platform Ops',
  'lic-admin': 'Licensor Admin',
  'lic-approver': 'Rights Approver',
  'lic-content': 'Content Manager',
  'buyer-admin': 'Licensee Admin',
  'buyer-user': 'Licensee User',
};
export const roleLabel = (role: string | null): string =>
  role ? ROLE_LABELS[role] ?? role : NO_ROLE;

// ---- The opened row's account actions (29 Oct, PR (b) B6). DRAFT (Vanja) ----
// The screen offers only the actions the server lists for this person, each enabled or with the server's reason, which
// is a code worded here. Role names come from ROLE_LABELS. Every change that takes something away asks for the person's
// email address to be typed.

// A company with its kind, as in the user list ("Young Hollywood, Licensor company"); in a dialog the id is in brackets.
export const companyWithKind = (company: string, kind: 'buyer' | 'licensor'): string => `${company}, ${companyKindLabel(kind)}`;

export const ACCOUNT_LOADING = 'Loading their access...';
export const ACCOUNT_LOAD_FAILED = "Their access couldn't be loaded.";
export const ACCOUNT_TRY_AGAIN = 'Try again';
export const ACCOUNT_STATE_LABEL = 'Account';
export const ACCOUNT_STATE_TEXT: Record<string, string> = {
  active: 'Active',
  disabled: 'Disabled: they cannot sign in',
  marked: 'Marked for deletion: they cannot sign in',
  deleting: 'Being deleted for good',
  deleted: 'Deleted',
  gone: 'Deleted, but the clean-up did not finish',
};
export const accountDeletableFrom = (date: string): string => `It can be deleted for good from ${date}.`;
export const ACCOUNT_NOT_AVAILABLE = 'Not available now';
export const ACCOUNT_COMPANY_FIXED_NOTE = 'To move them to another company, use Change company.';
export const ACCOUNT_MOVE_FIRST =
  'Move this account to a company first: its company is not on the company list, so a role cannot be given on it.';
export const ACCOUNT_NO_EMAIL = 'This account has no email address, so changes that need it typed cannot be confirmed here.';

export const ACCOUNT_ACTION_LABELS: Record<string, string> = {
  company: 'Change company',
  remove: 'Remove role',
  disable: 'Disable account',
  enable: 'Re-enable account',
  mark: 'Mark for deletion',
  delete: 'Delete for good',
};
export const ACCOUNT_ACTION_FINISH_DELETE = 'Finish deleting';

// Why an action is not available: the server's code, in words. An action's own words first, then the code's.
const ACCOUNT_REASON_BY_ACTION: Record<string, string> = {
  'company:NO_ROLE': 'This account has no role and no company. Give a role instead.',
  'remove:NO_ROLE': 'This account has no role to remove.',
  'disable:DELETE_IN_PROGRESS': 'A permanent delete of this account has started.',
  'enable:DELETE_IN_PROGRESS': 'A permanent delete started less than 15 minutes ago. Wait 15 minutes, then try again.',
  'delete:DELETE_IN_PROGRESS': 'A permanent delete started less than 15 minutes ago. Wait 15 minutes, then try again.',
  'mark:DELETE_IN_PROGRESS': 'A permanent delete of this account has started.',
};
export const ACCOUNT_REASON_TEXT: Record<string, string> = {
  CANNOT_CHANGE_OWN_ROLE: 'You cannot change your own account here.',
  CANNOT_CHANGE_OWN_ACCOUNT: 'You cannot change your own account here.',
  CANNOT_CHANGE_PLATFORM_ROLE: 'This is a platform account. Only an engineer can change it, with the claims script.',
  PLATFORM_ACCOUNT: 'This is a platform account. Only an engineer can change it, with the claims script.',
  ACCOUNT_DISABLED: 'The account is disabled. Re-enable it first.',
  EMAIL_NOT_VERIFIED: 'They have not confirmed their email address yet.',
  NO_ROLE: 'This account has no role.',
  ALREADY_DISABLED: 'The account is already disabled.',
  NOT_DISABLED: 'The account is not disabled.',
  DELETE_IN_PROGRESS: 'A permanent delete of this account has started.',
  ALREADY_MARKED: 'The account is already marked for deletion.',
  NOT_MARKED: 'Mark the account for deletion first.',
  DELETE_TOO_EARLY: 'It cannot be deleted for good yet: 30 days must pass after the mark.',
  NOT_DELETABLE_HOLDS_ROLE: 'This account has a role, so it can only be disabled.',
  NOT_DELETABLE_HELD_ROLE: 'This account has held a role, so it can only be disabled.',
  NOT_DELETABLE_PLATFORM_ROLE: 'This account has held a role, so it can only be disabled.',
  NOT_DELETABLE_HAS_CLAIMS: 'This account carries access settings, so it can only be disabled.',
  NOT_DELETABLE_HAS_ORDERS: 'This account has purchases or records that must be kept, so it can only be disabled.',
  NOT_DELETABLE_HAS_CUSTOMER_RECORD: 'This account has purchases or records that must be kept, so it can only be disabled.',
  NOT_DELETABLE_HAS_MODIFICATIONS: 'This account has purchases or records that must be kept, so it can only be disabled.',
  NOT_DELETABLE_HAS_PAYMENT_RECORDS: 'This account has purchases or records that must be kept, so it can only be disabled.',
  NOT_DELETABLE_HAS_CONTRACTS: 'This account has purchases or records that must be kept, so it can only be disabled.',
  NOT_DELETABLE_HAS_VIDEOS: 'This account has uploaded videos, so it can only be disabled.',
  HISTORY_UNAVAILABLE: "Its records couldn't all be checked just now. Try again later.",
  ACCOUNT_GONE: 'The account itself no longer exists.',
  MOVE_FIRST: 'Move this account to a company first.',
  NO_EMAIL: 'This account has no email address to type.',
  GONE_NO_EMAIL: 'The email address this account had is not known on this screen, so the clean-up cannot be confirmed here.',
};
export const ACCOUNT_REASON_UNKNOWN = 'Not available for this account.';
export const accountReasonText = (action: string, code: string | null): string =>
  (code && (ACCOUNT_REASON_BY_ACTION[`${action}:${code}`] ?? ACCOUNT_REASON_TEXT[code])) || ACCOUNT_REASON_UNKNOWN;
export const accountReasonLine = (action: string, reason: string): string => `${action}: ${reason}`;

// A sentence that names a person or a company, in parts (B6 review, 29 Oct): the name is a part of its own, which the
// screen isolates in <bdi> (users/Sentence.tsx), so a right-to-left name or a direction override in an email cannot
// reorder the words around it. `partsText` gives the plain sentence (for a test, or anywhere a string is needed).
export type TextPart = string | { name: string };
const named = (name: string): TextPart => ({ name });
export const partsText = (parts: TextPart[]): string => parts.map((part) => (typeof part === 'string' ? part : part.name)).join('');

// The "type their email" label, with the address isolated (the label of the confirmation's field).
export const typeEmailLabelParts = (email: string): TextPart[] => ['Type ', named(email), ' to confirm'];

// The role form's dialogs and done line on the searchable list (the same words as PR (a)'s string versions above, in
// parts).
export const grantDialogParts = (who: string, role: string, company: string): TextPart[] => [
  named(who),
  ` will have the role ${role} on `,
  named(company),
  '. They will need to sign in again before it takes effect.',
];
export const replaceDialogParts = (who: string, from: string, to: string, company: string): TextPart[] => [
  named(who),
  ` has the role ${from}. It will be replaced by ${to} on `,
  named(company),
  `, which takes away what ${from} allowed. They will need to sign in again.`,
];
export const accessDoneParts = (who: string, role: string, company: string): TextPart[] => [
  named(who),
  ` now has the role ${role} on `,
  named(company),
  '. They will need to sign in again.',
];

// The confirmations. Each names the person and what changes, and asks for their email address to be typed.
export const ACCOUNT_DIALOG_CANCEL = 'Cancel';
export const REMOVE_DIALOG_TITLE = 'Remove this role?';
export const removeDialogBody = (who: string, role: string, company: string): TextPart[] => [
  named(who),
  ` will lose the role ${role} and be signed out. They keep their company, `,
  named(company),
  ', and will wait for a new role.',
];
export const REMOVE_DIALOG_CONFIRM = 'Remove role';
export const COMPANY_DIALOG_TITLE = 'Move to another company?';
export const companyDialogBody = (who: string, from: string, to: string, role: string): TextPart[] => [
  named(who),
  ' will move from ',
  named(from),
  ' to ',
  named(to),
  `. Their role (${role}) stays. They will be signed out and need to sign in again.`,
];
export const COMPANY_DIALOG_CONFIRM = 'Move';
export const COMPANY_FORM_KIND = 'Kind of company';
export const COMPANY_FORM_NO_ROLE_NOTE = 'They have no role, so either kind of company can be chosen. A later role must match its kind.';
export const companyFormRoleNote = (role: string, kind: 'buyer' | 'licensor'): string =>
  `Their role (${role}) needs a ${companyKindLabel(kind).toLowerCase()}.`;
export const COMPANY_FORM_APPLY = 'Move';
export const DISABLE_DIALOG_TITLE = 'Disable this account?';
export const disableDialogBody = (who: string): TextPart[] => [
  named(who),
  ' will be signed out at once and cannot sign in until the account is re-enabled. Their role and company stay as they are.',
];
export const DISABLE_DIALOG_CONFIRM = 'Disable account';
export const ENABLE_DIALOG_TITLE = 'Re-enable this account?';
export const enableDialogBody = (who: string): TextPart[] => [
  named(who),
  ' will be able to sign in again, with the same role and company as before. If the account was marked for deletion, the mark is cancelled.',
];
export const ENABLE_DIALOG_CONFIRM = 'Re-enable account';
export const MARK_DIALOG_TITLE = 'Mark this account for deletion?';
export const markDialogBody = (who: string): TextPart[] => [
  named(who),
  ' will be signed out and cannot sign in. After 30 days the account can be deleted for good. Re-enable it before then to cancel.',
];
export const MARK_DIALOG_CONFIRM = 'Mark for deletion';
export const DELETE_DIALOG_TITLE = 'Delete this account for good?';
export const deleteDialogBody = (who: string): TextPart[] => [
  'The account of ',
  named(who),
  ', its sign-in and its profile will be deleted. This cannot be undone.',
];
export const DELETE_DIALOG_CONFIRM = 'Delete for good';
export const FINISH_DELETE_DIALOG_TITLE = 'Finish deleting this account?';
export const finishDeleteDialogBody = (who: string): TextPart[] => [
  'The account ',
  named(who),
  ' was deleted, but its clean-up did not finish. Type the email address it had to finish it.',
];
export const FINISH_DELETE_DIALOG_CONFIRM = 'Finish deleting';

// What happened.
export const accountDoneText: Record<string, (who: string, company: string) => TextPart[]> = {
  company: (who, company) => [named(who), ' is now on ', named(company), '. They will need to sign in again.'],
  remove: (who, company) => [named(who), ' no longer has a role. They keep their company, ', named(company), '.'],
  disable: (who) => [named(who), ' is disabled and signed out.'],
  enable: (who) => [named(who), ' can sign in again.'],
  mark: (who) => [named(who), ' is marked for deletion and signed out.'],
};
export const accountDeleted = (who: string): TextPart[] => [named(who), ' was deleted for good.'];
export const ACCOUNT_SESSIONS_ENDED = 'Their existing sessions are now ended.';
// A change answered "sessions not ended" (500 SESSIONS_NOT_ENDED): the change WAS made (the gateway says so), and what
// that means for the person (B6 review, attacker M2; the gateway's words since 29 Oct say the same).
export const ACCOUNT_SESSIONS_NOT_ENDED =
  'The change was made, but their existing sessions could not be ended: they can keep their old access until they sign out, for up to 24 hours.';
export const ACCOUNT_SESSIONS_PENDING =
  'Their existing sessions were not ended after an earlier change: they can keep their old access until they sign out, for up to 24 hours.';
export const ACCOUNT_SESSIONS_RETRY = 'Make the same change again to end them';
export const ACCOUNT_SESSIONS_DISABLE_ADVICE = 'Disable the account, then re-enable it, to end them.';
export const SESSIONS_DIALOG_TITLE = 'End their sessions?';
export const sessionsDialogBody = (who: string): TextPart[] => [
  'The same change is made again for ',
  named(who),
  ', which ends their existing sessions. They will need to sign in again.',
];
export const SESSIONS_DIALOG_CONFIRM = 'End sessions';
// The person was read again right before the repeat and is no longer as the dialog showed them: nothing was sent.
export const ACCOUNT_REPEAT_CHANGED =
  "The change was not made. The person's access changed since this dialog opened. Their current state is shown here; check it before trying again.";
// A change whose outcome is not known (a server error, a lost answer, "started but not finished"): never "not made".
export const ACCOUNT_MAYBE_DONE = 'The change may have been made. Their current state is shown here; check it before trying again.';
// A delete that stopped while the account itself still exists: a new try is refused for 15 minutes.
export const ACCOUNT_DELETE_MAYBE_DONE =
  'The delete may have been made, or it may have stopped part way. If it did not finish, try again in 15 minutes: a new try is refused until then.';
// A delete that stopped after the account itself was removed: the clean-up can be finished at once (B6 review, M1).
export const ACCOUNT_DELETE_GONE_FINISH =
  'The account itself was deleted, but the clean-up did not finish. Use Finish deleting to finish it now.';
// A delete the gateway refused after it had started: it put the account back to disabled and cleared the mark (B6
// review, attacker L2; adminAccount.service.ts releaseStuckDelete and the re-check's STATE_CHANGED).
export const ACCOUNT_DELETE_MARK_CLEARED =
  'Not deleted. The mark was cleared and the account is disabled; mark it again to start the 30 days again.';
// The read that follows an action failed: what was shown stays, and Try again reads again.
export const ACCOUNT_REREAD_FAILED = "Their current state couldn't be loaded after the change.";
// A refusal's code, in words (the gateway's codes; a reason code above is used when there is none here).
export const ACCOUNT_ERROR_TEXT: Record<string, string> = {
  CONFIRM_EMAIL_MISMATCH: 'The email address typed does not match theirs.',
  HISTORY_UNAVAILABLE: "Their records couldn't all be checked just now. Try again.",
  ADMIN_RECORD_UNAVAILABLE: "The change couldn't be recorded just now. Try again.",
  // A re-enable ends the old sessions first, and refuses when it cannot (the gateway, audit P-1, 29 Oct).
  SESSIONS_UNAVAILABLE: "Their old sessions couldn't be ended first, so the account was not re-enabled. Try again.",
  STATE_CHANGED: 'This account changed while you were acting on it. Its current state is shown here.',
  SAME_TENANT: 'The account is already on that company.',
  TENANT_NOT_FOUND: 'That company is not on the company list.',
  TENANT_KIND_MISMATCH: "That company is the wrong kind for their role.",
  TENANT_REQUIRED: 'Choose a company.',
  TENANT_ID_INVALID: 'That company id is not valid.',
  USER_NOT_FOUND: 'This account no longer exists.',
  CLAIMS_TOO_LARGE: "That change would make the account's access settings too large.",
  BODY_INVALID: 'That request was not accepted.',
  INVALID_USER_ID: 'That request was not accepted.',
};
export const accountErrorText = (action: string, code: string | undefined): string | null =>
  code ? ACCOUNT_ERROR_TEXT[code] ?? ACCOUNT_REASON_BY_ACTION[`${action}:${code}`] ?? ACCOUNT_REASON_TEXT[code] ?? null : null;

// ---------------------------------------------------------------------------
// Videos (the admin video manager, 2026-10-26)
// ---------------------------------------------------------------------------
// Every word the Videos section shows. The server sends codes (a video's display
// state, its actions, a bulk verdict, a result's outcome, an error); the screen
// only looks them up here, and shows the code itself for one it does not know.
// Draft wording: Vanja owns the final text.
export const SECTION_VIDEOS = 'Videos';
export const VIDEOS_HELP =
  'Every video the platform holds. Removing a video hides it at once; restore it any time before its permanent deletion.';
export const VIDEO_SEARCH_LABEL = 'Search by title, video id or JW media id';
export const VIDEO_SEARCH_BUTTON = 'Search';
export const VIDEO_SEARCH_CLEAR = 'Show all';
export const VIDEO_FILTER_STATUS = 'Status';
export const VIDEO_FILTER_REMOVAL = 'Removal';
export const VIDEO_FILTER_SOURCE = 'Source';
export const VIDEO_FILTER_ANY = 'Any';
export const VIDEOS_LOAD_MORE = 'Load more';
export const VIDEOS_EMPTY = 'No videos match.';
export const videosShown = (shown: number, total: number | null): string =>
  total === null ? `${shown} videos` : `${shown} of ${total} videos`;

export const VIDEO_COL_PICTURE = 'Picture';
export const VIDEO_COL_TITLE = 'Title';
export const VIDEO_COL_ID = 'Video id';
export const VIDEO_COL_JW = 'JW media id';
export const VIDEO_COL_STATE = 'State';
export const VIDEO_COL_OWNER = 'Owner';
export const VIDEO_COL_SOURCE = 'Source';
export const VIDEO_COL_CREATED = 'Added';
export const VIDEO_COL_LENGTH = 'Length';
export const VIDEO_COL_ACTIONS = 'Actions';
export const VIDEO_COL_STATUS = 'Status';
export const VIDEO_UNTITLED = 'Untitled';
export const VIDEO_NONE = 'None';
export const videoPictureAlt = (title: string | null): string => `Picture for ${title || VIDEO_UNTITLED}`;
export const videoDuration = (seconds: number | null): string => {
  if (seconds === null) return VIDEO_NONE;
  const whole = Math.round(seconds);
  return `${Math.floor(whole / 60)}:${String(whole % 60).padStart(2, '0')}`;
};
export const videoDate = (iso: string | null): string => (iso ? iso.slice(0, 10) : VIDEO_NONE);

// A video's display state, as the server names it. PROPOSED 27 Oct (Joey: no "On sale"; waiting for his OK on the
// wording): a Ready video that is not removed reads "Live", and a failed one plain "Failed" (a Failed video may be a
// pipeline failure or a retired duplicate, not only the quality gate; Details shows the reason).
export const VIDEO_STATE_LABELS: Record<string, string> = {
  on_sale: 'Live',
  processing: 'Processing',
  failed: 'Failed',
  archived: 'Archived: kept for good because an order or AI modification uses it',
  purging: 'Being deleted',
  purged: 'Deleted',
  // Joey, 27 Oct: plain words for VPM's purge_blocked (the master is outside the videos bucket; nothing was deleted,
  // and it can be restored).
  purge_blocked: 'Deletion stopped, nothing deleted',
  removed: 'Removed',
  removal_unknown: 'Removed (unknown state)',
  unknown: 'Unknown state',
};
// A state the server sends with a date (a removed video's permanent deletion date): the label carries the date.
const VIDEO_STATE_DATED_LABELS: Record<string, (date: string) => string> = {
  removed: (date) => `Removed. Permanent on ${date}`,
};
export const videoStateLabel = (state: string, purgeAfter: string | null): string => {
  const dated = VIDEO_STATE_DATED_LABELS[state];
  if (dated && purgeAfter) return dated(purgeAfter.slice(0, 10));
  return VIDEO_STATE_LABELS[state] ?? state;
};
// A video's pipeline status in the bulk review (PROPOSED 27 Oct, as the row states above).
export const VIDEO_STATUS_LABELS: Record<string, string> = { Ready: 'Live', Processing: 'Processing', Failed: 'Failed' };
// The Status filter offers only the statuses named here (Joey, 27 Oct: no "On sale"; Ready reads "Live", and the list
// starts on it). A status the server adds later appears in the filter only once it is named here.
// Choosing a status lists only videos that are not removed (VPM marks a removed video Failed): VideosSection.
export const VIDEO_STATUS_FILTER_LABELS: Record<string, string> = { Ready: 'Live', Processing: 'Processing', Failed: 'Failed' };
export const VIDEO_REMOVAL_FILTER_LABELS: Record<string, string> = {
  none: 'Not removed',
  removed: 'Removed',
  archived: 'Archived',
  purging: 'Being deleted',
  purged: 'Deleted',
  purge_blocked: 'Deletion stopped',
};
export const VIDEO_SOURCE_LABELS: Record<string, string> = { bulk_transfer: 'JW import', upload: 'Browser upload', backfill: 'Backfill' };
export const videoSourceLabel = (source: string | null): string => (source ? VIDEO_SOURCE_LABELS[source] ?? source : VIDEO_NONE);
export const labelOf = (labels: Record<string, string>, value: string): string => labels[value] ?? value;

export const VIDEO_ACTION_LABELS: Record<string, string> = { remove: 'Remove', restore: 'Restore' };
export const VIDEO_DETAILS = 'Details';
export const VIDEO_CLOSE = 'Close';
export const VIDEO_CANCEL = 'Cancel';

// The reasons an admin chooses from. The codes are the server's.
export const REASON_CODE_LABELS: Record<string, string> = {
  duplicate: 'Duplicate of another video',
  not_for_platform: 'Should not be on the platform (rough cut, screen recording, timecode)',
  quality_issue: 'Quality issue',
  rights: 'Rights issue',
  other: 'Other',
};
export const VIDEO_REASON_CODE_LABEL = 'Reason';
export const VIDEO_REASON_TEXT_LABEL = 'Details of the reason';
export const videoReasonTextHelp = (min: number, max: number): string => `${min} to ${max} characters.`;
export const VIDEO_TYPE_ID_LABEL = 'Type the video id to confirm';

// Remove one video. The soft-delete wording (Joey, 23 Oct): the permanent
// deletion happens after the removal window, and is final after a further
// 7-day recovery window. Since Igor's review of #82 (Joey, 26 Oct) the 7-day
// line is its own "Not a restore:" paragraph, so it cannot be read as the
// restore window; the window itself is shown only when the server sends it.
export const VIDEO_REMOVE_TITLE = 'Remove this video?';
export const VIDEO_REMOVE_BODY =
  'The video is hidden at once: it leaves search and the shop, and its page is refused to everyone but Platform Admin. Buyers keep their clips and contracts. You can restore it until its removal window ends; then it is permanently deleted, or kept for good if any order or AI modification uses it.';
export const VIDEO_NOT_A_RESTORE =
  'Not a restore: after a permanent deletion an engineer can still recover the files for 7 days; after that the deletion is final.';
// The restore window, from the server's days, and the date a removal now would become permanent.
export const videoRemoveWindow = (days: number, date: string): string =>
  `You can restore it for ${days} days. Removed now, it will be permanently deleted on or after ${date} unless you restore it before then.`;
export const videoBulkRemoveWindow = (days: number, date: string): string =>
  `You can restore them for ${days} days. Removed now, they will be permanently deleted on or after ${date} unless restored before then.`;
/**
 * The day a removal made at `now` becomes permanent, `days` later, as YYYY-MM-DD in the admin's own calendar (local
 * time), so "on or after" never gives a western admin an extra day. '' for a date the browser cannot represent.
 */
export const removalDateFrom = (days: number, now: Date): string => {
  const at = new Date(now.getTime() + days * 24 * 60 * 60 * 1000);
  if (!Number.isFinite(at.getTime())) return '';
  return `${at.getFullYear()}-${String(at.getMonth() + 1).padStart(2, '0')}-${String(at.getDate()).padStart(2, '0')}`;
};
export const VIDEO_REMOVE_CONFIRM = 'Remove video';
export const videoRemovedStatus = (title: string, purgeAfter: string | undefined): string =>
  purgeAfter
    ? `${title} was removed. It will be permanently deleted on ${purgeAfter.slice(0, 10)} unless you restore it.`
    : `${title} was removed.`;

// Restore
export const VIDEO_RESTORE_TITLE = 'Restore this video?';
export const VIDEO_RESTORE_BODY =
  'The video goes back to exactly how it was before it was removed, at once and with no reprocessing.';
export const VIDEO_RESTORE_CONFIRM = 'Restore video';
export const videoRestoredStatus = (title: string): string => `${title} was restored.`;

// Selecting videos and removing them together (Joey, 27 Oct: tick rows, not paste ids or choose a file). PROPOSED
// wording, waiting for his OK. No "select all": each video is ticked on purpose.
export const VIDEO_COL_SELECT = 'Select';
export const videoSelectRow = (title: string | null): string => `Select ${title || VIDEO_UNTITLED}`;
export const videosSelected = (count: number, max: number): string =>
  `${count} selected (at most ${max} at a time)`;
export const VIDEO_CLEAR_SELECTION = 'Clear selection';
export const videoRemoveSelected = (count: number): string => (count > 0 ? `Remove selected (${count})` : 'Remove selected');
export const videoBulkTitle = (count: number): string => (count === 1 ? 'Remove 1 video' : `Remove ${count} videos`);
export const VIDEO_BULK_SELECTED_HELP = 'These are the videos you ticked. Choose a reason, then review them.';
export const videoBulkReferencesWarning = (count: number): string =>
  count === 1
    ? '1 of these videos has orders or AI modification requests. It will be hidden now and kept for good, never deleted.'
    : `${count} of these videos have orders or AI modification requests. They will be hidden now and kept for good, never deleted.`;
export const videoBulkChecksFailedWarning = (count: number): string =>
  count === 1
    ? 'For 1 of these videos the orders could not be checked. It will not be deleted until they are.'
    : `For ${count} of these videos the orders could not be checked. They will not be deleted until they are.`;
export const VIDEO_BULK_REVIEW = 'Review';
export const VIDEO_BULK_REVIEW_HELP = 'The server checked each video. Only the ones marked "Will be removed" are removed.';
export const VIDEO_COL_VERDICT = 'What happens';
export const VERDICT_LABELS: Record<string, string> = {
  will_remove: 'Will be removed',
  already_removed: 'Already removed',
  not_found: 'Not found',
  processing_not_allowed: 'Still processing: will be skipped',
  unknown: 'Unknown',
};
export const VIDEO_REFERENCES_FLAG = 'Has orders or AI modifications: it will be kept hidden for good, never deleted.';
export const VIDEO_REFERENCES_CHECK_FAILED = 'Orders could not be checked: it will not be deleted until they are.';
export const VIDEO_BULK_CONTINUE = 'Continue';
export const VIDEO_BULK_BACK = 'Back';
export const VIDEO_BULK_SURE_TITLE = 'Are you sure?';
export const videoBulkSureBody = (count: number): string =>
  `${count} videos will be hidden at once and permanently deleted when their removal window ends, unless restored.`;
export const VIDEO_BULK_SURE_YES = 'Yes, continue';
export const VIDEO_BULK_SURE_NO = 'No, go back';
export const VIDEO_BULK_FINAL_TITLE = 'Confirm the bulk remove';
export const videoBulkShowList = (count: number): string => `Show the ${count} videos`;
export const videoBulkHideList = (count: number): string => `Hide the ${count} videos`;
export const VIDEO_BULK_TYPE_COUNT_LABEL = 'Type the number of videos to confirm';
export const videoBulkReason = (label: string, text: string): string => `Reason: ${label}. ${text}`;
export const VIDEO_BULK_CONFIRM = 'Remove videos';
export const VIDEO_BULK_RESULTS_TITLE = 'Results';
export const OUTCOME_LABELS: Record<string, string> = {
  removed: 'Removed',
  already_removed: 'Already removed',
  processing_not_allowed: 'Still processing: not removed',
  not_found: 'Not found',
  request_already_used: 'Not removed: it was restored after an earlier removal',
  not_attempted: 'Not attempted: review and remove it again',
  failed: 'Failed',
  unknown: 'Unknown: check this video in the list',
};
export const VIDEO_BULK_UNCERTAIN =
  'The video service did not finish its report, so some of these may have been removed. Check each one in the list.';
// The bulk request was sent but no answer came back (a timeout, a lost connection, a server error): the gateway may
// already have spent the review and removed some videos, so the screen never offers the same confirm again.
export const VIDEO_BULK_NO_ANSWER =
  'No answer came back, so some or all of these videos may have been removed. Check each one in the list before you try again.';
export const VIDEO_BULK_NO_ANSWER_OUTCOME = 'No answer: check this video in the list';
export const VIDEO_OPTIONS_RETRY = 'Try again';

// Details and the audit view
export const VIDEO_DETAIL_TITLE = 'Video details';
export const videoReferencesText = (orders: number | null, modifications: number | null): string =>
  `${orders ?? VIDEO_NONE} orders, ${modifications ?? VIDEO_NONE} AI modifications`;
export const VIDEO_STATUS_REASON = 'Status reason';
export const VIDEO_REMOVAL_REASON = 'Removal reason';
export const VIDEO_REMOVED_BY = 'Removed by';
export const VIDEO_AUDIT_TITLE = 'Audit';
export const VIDEO_AUDIT_EMPTY = 'No audit rows yet.';
export const AUDIT_COL_WHEN = 'When';
export const AUDIT_COL_ACTION = 'Action';
export const AUDIT_COL_WHO = 'Who';
export const AUDIT_COL_REASON = 'Reason';
export const AUDIT_ACTION_LABELS: Record<string, string> = {
  remove: 'Removed',
  restore: 'Restored',
  archive: 'Archived',
  purge: 'Permanently deleted',
  purge_failed: 'Permanent deletion failed, will retry',
  purge_blocked: 'Deletion stopped, nothing deleted',
};

// Errors: the server's code, or the client's kind, in words.
const ADMIN_VIDEOS_ERROR_TEXT: Record<string, string> = {
  forbidden: 'You do not have permission to manage videos.',
  service_unavailable: 'The video service is not set up for this environment.',
  catalogue_unavailable: 'The video list is still loading. Try again in a moment.',
  upstream_error: 'The video service did not answer. Try again.',
  upstream_timeout: 'The video service took too long to answer. Try again.',
  not_found: 'That video was not found.',
  bad_video_id: 'That is not a video id.',
  bad_body: 'That request was not accepted.',
  bad_query: 'That search or filter was not accepted.',
  bad_reason: 'Choose a reason, and give details within the length shown under the field.',
  duplicate_ids: 'The list holds the same video twice.',
  too_many_ids: 'Too many videos at once.',
  confirmation_mismatch: 'The typed confirmation does not match.',
  unsupported_media_type: 'That request was not accepted.',
  too_large: 'That request was too large.',
  preview_token_invalid: 'This review has expired or was already used. Review the list again.',
  bulk_in_progress: 'Another bulk remove of yours is still running. Wait for it to finish.',
  already_removed: 'That video is already removed.',
  request_already_used: 'That removal was already made once and the video was restored since.',
  processing_not_allowed: 'That video is still processing and cannot be removed yet.',
  not_removed: 'That video is not removed.',
  purge_started: 'Its permanent deletion has already started, so it cannot be restored.',
  unknown_state: 'That video is in a state that cannot be restored.',
  no_previous_status: 'That video cannot be restored automatically.',
  conflict: 'That change was refused.',
};
const ADMIN_VIDEOS_KIND_TEXT: Record<string, string> = {
  forbidden: ADMIN_VIDEOS_ERROR_TEXT.forbidden,
  auth: 'Your session has expired. Sign in again.',
  timeout: 'The request timed out. Try again.',
  network: 'No answer from the server. Check your connection and try again.',
  disabled: 'The video manager is not set up for this build.',
  'not-found': ADMIN_VIDEOS_ERROR_TEXT.not_found,
  conflict: ADMIN_VIDEOS_ERROR_TEXT.conflict,
  unavailable: ADMIN_VIDEOS_ERROR_TEXT.upstream_error,
  invalid: ADMIN_VIDEOS_ERROR_TEXT.bad_body,
};
export const ADMIN_VIDEOS_ERROR_FALLBACK = 'Something went wrong. Try again.';
export const adminVideosErrorText = (kind: string | undefined, code: string | undefined): string =>
  (code && ADMIN_VIDEOS_ERROR_TEXT[code]) || (kind && ADMIN_VIDEOS_KIND_TEXT[kind]) || ADMIN_VIDEOS_ERROR_FALLBACK;

// ---- Configurable territories (2026-10, dev only) -------------------------------------------------------------------------------------
export const TERRITORY_PANEL_TITLE = 'Territory';
export const TERRITORY_MODE_LABEL = 'Where buyers may license this owner’s clips';
export const TERRITORY_MODE_DOMESTIC = 'Domestic only (the US)';
export const TERRITORY_MODE_WORLDWIDE = 'Domestic or worldwide (the default)';
export const TERRITORY_MODE_CONFIGURABLE = 'Regions and countries I price';
export const TERRITORY_HELP =
  'A buyer pays the sum of the territories they pick, never more than the worldwide price above. A territory you do not price is not offered. A region counts as one item. Prices are USD per second; leave a price blank to not offer that usage and period.';
export const TERRITORY_COL_TERRITORY = 'Territory';
export const TERRITORY_COL_ALL_MEDIA_ONE_YEAR = 'All Media, 1 Year';
export const TERRITORY_COL_ALL_MEDIA_PERPETUITY = 'All Media, Perpetuity';
export const TERRITORY_COL_AI_ONE_YEAR = 'All Media + AI, 1 Year';
export const TERRITORY_COL_AI_PERPETUITY = 'All Media + AI, Perpetuity';
export const TERRITORY_ADD_LABEL = 'Add a territory';
export const TERRITORY_GROUP_REGIONS = 'Regions';
export const TERRITORY_GROUP_COUNTRIES = 'Countries';
export const TERRITORY_REMOVE = (name: string): string => `Remove ${name}`;
export const TERRITORY_NONE_YET = 'No territory is priced yet. Add one to offer it to buyers.';
export const TERRITORY_NEEDS_ONE = 'Price at least one territory before saving, or choose another option.';
export const TERRITORY_ERROR_NOTHING_PRICED = 'Price at least one of the four, or remove this territory.';
export const TERRITORY_ERROR_NOT_A_NUMBER = 'Enter a number.';
export const TERRITORY_ERROR_NEGATIVE = 'A price cannot be negative.';
export const TERRITORY_NOT_SAVED = (reason: string): string => `Not saved: ${reason}`;

export const SECTION_TERRITORIES = 'Territories';
export const TERRITORY_CONFIG_HELP =
  'Which countries each region holds, and which countries are blocked. A country belongs to one region only. Owners can price a region or a single country; a blocked country, and any region that holds one, cannot be priced or bought. Saving replaces the whole list.';
export const TERRITORY_CONFIG_NOT_ENABLED = 'Territory pricing is not enabled in this environment.';
export const TERRITORY_CONFIG_NOT_SEEDED = 'No territories have been set up yet. Enter the countries of each region and save to create the first version.';
export const TERRITORY_CONFIG_REGION_LABEL = (name: string): string => `${name}: countries`;
export const TERRITORY_CONFIG_BLOCKED_LABEL = 'Blocked countries';
export const TERRITORY_CONFIG_CODES_HINT = 'Two-letter country codes separated by spaces or commas, for example FR DE IT.';
export const TERRITORY_CONFIG_VERSION = (version: string): string => `Version ${version}`;
export const TERRITORY_CONFIG_REGION_EMPTY = (name: string): string => `${name} needs at least one country.`;
export const TERRITORY_CONFIG_BAD_CODE = (code: string, where: string): string => `“${code}” in ${where} is not a country code.`;
export const TERRITORY_CONFIG_TWICE = (code: string, where: string): string => `${code} is listed twice in ${where}.`;
export const TERRITORY_CONFIG_TWO_REGIONS = (code: string, first: string, second: string): string =>
  `${code} is in both ${first} and ${second}: a country belongs to one region only.`;
export const TERRITORY_CONFIG_BLOCKED_IN_REGION_NOTE =
  'A blocked country may also sit in a region; that region then cannot be priced or bought.';

export const TERRITORY_PRICE_KEY_LABEL: Record<string, string> = {
  'all_media|one_year': 'All Media, 1 Year',
  'all_media|perpetuity': 'All Media, Perpetuity',
  'all_media_ai|one_year': 'All Media + AI, 1 Year',
  'all_media_ai|perpetuity': 'All Media + AI, Perpetuity',
};
export const TERRITORY_FREE_CONFIRM = (list: string): string => `I confirm these prices are free (0), so buyers pay nothing for them: ${list}`;
export const TERRITORY_FREE_WARNING = 'A price of 0 makes that territory free. Leave a price blank to not offer it.';
